import {useEffect, useMemo, useState} from 'react';

import moment, {unitOfTime} from 'moment-tz';

import {gts} from 'app/utils/i18n';

type TaskDurationType = {
  type: unitOfTime.Diff;
  value: number;
};

export function useTaskStartTime(
  due: string | null | undefined,
  dtstart: string | null | undefined,
  onDtstartChange: (dtstart: string | null | undefined) => unknown
) {
  const durationTypes = useMemo(getDurationTypes, []);
  const duration = useMemo(() => getTaskDuration(due, dtstart), [dtstart]);

  // recalculate dtstart from (due - duration) when due date changes
  useEffect(() => {
    const currDuration = getTaskDuration(due, dtstart);

    if (
      currDuration.type != duration.type ||
      currDuration.value != duration.value
    ) {
      applyDuration(duration);
    }
  }, [due]);

  const [isStartTimeFormVisible, setStartTimeFormVisible] = useState(
    duration.value > 0
  );

  function setDuration(update: Partial<TaskDurationType>) {
    const updated = {...duration, ...update};

    // do not allow values lower than zero
    const value = Math.max(0, updated.value);

    applyDuration({value, type: updated.type});
  }

  function applyDuration(d: TaskDurationType) {
    const updatedDtstart = moment(due).subtract(d.value, d.type).toISOString();
    onDtstartChange(updatedDtstart);
  }

  return {
    duration,
    setDuration,
    durationTypes,
    setStartTimeFormVisible,
    isStartTimeFormVisible,
  };
}

function getTaskDuration(
  dueStr: string | null | undefined,
  dtstartStr: string | null | undefined
): TaskDurationType {
  const zeroDuration: TaskDurationType = {
    type: 'minute',
    value: 0,
  };
  // we need extract the dtstart and due difference only when both
  // were defined (i.e. when reassigning an existing task)
  if (dueStr == null || dtstartStr == null) {
    return zeroDuration;
  }

  let timeDurations = getDurationTypes()
    .map((u) => u.key)
    .reverse();

  let due = moment(dueStr);
  let dtstart = moment(dtstartStr);

  // try to find the exact difference between due and dtstart
  // using the timeDurations items as time unit
  for (const type of timeDurations) {
    let value = due.diff(dtstart, type, true);

    if (value && parseInt(String(value)) === value) {
      return {
        type,
        value,
      };
    }
  }

  return zeroDuration;
}

export const getDurationTypes = (): Array<{
  key: unitOfTime.Diff;
  label: string;
}> => [
  {
    key: 'minute',
    label: gts('Minutes'),
  },
  {
    key: 'hour',
    label: gts('Hours'),
  },
  {
    key: 'day',
    label: gts('Days'),
  },
  {
    key: 'week',
    label: gts('Weeks'),
  },
];
